/**
 * "GWC added" / "You added" on every quote line (quote rebuild slice C,
 * migration 716; approved mockup "Quote Editor Rebuild", Corbin 2026-10-28).
 *
 * GWC drafts the quote the way a self-driving car drives. When the office
 * takes over it should feel familiar, smooth and safe:
 *   - every line GWC added says so and says why, and comes off in one tap;
 *   - a line the office added or changed turns "You". From then on Tidy up,
 *     Re-price and Rebuild never change or remove it.
 *
 * Office-only. None of this reaches the customer's PDF, email or portal.
 */
import { Sparkles, UserRound } from "lucide-react";
import { InfoButton, type InfoTopic } from "@/components/info-button";
import { cn } from "@/lib/utils";

export interface OriginLine {
    added_by?: string | null;
    reason?: string | null;
    user_touched?: boolean | null;
}

export type LineOriginKind = "gwc" | "you_added" | "you_changed";

/**
 * Which tag a line shows. A person's line is "You added"; a GWC line a person
 * has since changed is "You changed" (it is theirs now); an untouched GWC line
 * is "GWC added". An older line nobody recorded (no added_by, never touched)
 * shows no tag.
 */
export function lineOrigin(line: OriginLine): LineOriginKind | null {
    if (line.added_by === "person") return "you_added";
    if (line.user_touched) return "you_changed";
    if (line.added_by === "gwc") return "gwc";
    return null;
}

/** True when the trash can removes the line in one tap (Undo puts it back). */
export function removesInOneTap(line: OriginLine): boolean {
    return lineOrigin(line) === "gwc";
}

export const LINE_ORIGIN_HELP: InfoTopic = {
    title: "GWC added and You added",
    lines: [
        "GWC added means the app put this line on the quote. The gray words say why.",
        "Don't want it? Tap the trash can. It comes off right away. Tap Undo to put it back.",
        "You added and You changed mean the line is yours. Tidy up, Re-price and Rebuild never change it or take it off.",
        "Customers never see these tags.",
    ],
};

/** The tag (and, for a GWC line, its reason) under a line's words. */
export function LineOriginTag({ line, className }: { line: OriginLine; className?: string }) {
    const kind = lineOrigin(line);
    if (!kind) return null;
    if (kind === "gwc") {
        return (
            <span className={cn("inline-flex flex-wrap items-center gap-1", className)} data-testid="line-origin">
                <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[11px] font-bold text-blue-700 dark:text-blue-300">
                    <Sparkles className="h-3 w-3" aria-hidden />
                    GWC added
                </span>
                {line.reason && (
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {line.reason}
                    </span>
                )}
            </span>
        );
    }
    return (
        <span
            className={cn(
                "inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-bold text-amber-800 dark:text-amber-300",
                className,
            )}
            title="This line is yours. Tidy up, Re-price and Rebuild leave it alone."
            data-testid="line-origin"
        >
            <UserRound className="h-3 w-3" aria-hidden />
            {kind === "you_added" ? "You added" : "You changed"}
        </span>
    );
}

/** The key under the list: what the two tags mean, with the ⓘ. */
export function LineOriginKey() {
    return (
        <div
            className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px] text-muted-foreground"
            data-testid="line-origin-key"
        >
            <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-1.5 py-0.5 font-bold text-blue-700 dark:text-blue-300">
                <Sparkles className="h-3 w-3" aria-hidden />
                GWC added
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 font-bold text-amber-800 dark:text-amber-300">
                <UserRound className="h-3 w-3" aria-hidden />
                You added
            </span>
            <span>Your lines stay put.</span>
            <InfoButton topic={LINE_ORIGIN_HELP} />
        </div>
    );
}
